import { useNavigate } from "react-router-dom";
import ROUTES from "../../routes";
import { Button, Form, Input, message } from "antd";
import { ACCESS_TOKEN_NAME, REFRESH_TOKEN_NAME } from "../../shared/constants";
import validateMessages from "../../utils/validate";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { useAuthLoginMutation } from "../../services/auth";
import { IApiResponse, ILoginResponse } from "../../types/User";
import { useEffect, useState } from "react";
import Password from "antd/es/input/Password";
import { useSaveMyFcmIdTokenMutation } from "../../services/me";
import { generateFirebaseToken } from "../../firebase";
import { useTranslation } from "react-i18next";

const Login = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [loginForm] = Form.useForm();
  const [authLogin, authLoginResult] = useAuthLoginMutation();
  const [loading, setLoading] = useState<boolean>();
  const [errorMessage, setErrorMessage] = useState<string | null | undefined>("");
  const [saveMyFcmToken] = useSaveMyFcmIdTokenMutation();
  const [systemLanguage, setSystemLanguage] = useState("en");

  useEffect(() => {
    const changeLanguageAsync = async () => {
      let lng = window.navigator.language || "en";
      lng = lng.split("-")?.[0];
      await i18n.changeLanguage(lng);
      setSystemLanguage(lng);
    };
    changeLanguageAsync();
  }, [window.navigator.language]);

  useEffect(() => {
    if (localStorage.getItem(ACCESS_TOKEN_NAME)) {
      navigate(ROUTES.PATHS.SL_HOME);
    }
  }, [localStorage.getItem(ACCESS_TOKEN_NAME)])

  const handleLogin = async () => {
    if (authLoginResult?.isLoading && loading) return;
    let result: IApiResponse<ILoginResponse> | null = null;
    try {
      setLoading(true);
      const formFields = loginForm.getFieldsValue();
      result = await authLogin(formFields).unwrap();
      loginForm.resetFields();

      if (!result?.error && result?.data?.accessToken) {
        localStorage.setItem(ACCESS_TOKEN_NAME, result?.data?.accessToken);
        if (result?.data?.refreshToken)
          localStorage.setItem(REFRESH_TOKEN_NAME, result?.data?.refreshToken);
        setErrorMessage(null);

        generateFirebaseToken((token: string) => {
          saveMyFcmToken({
            body: {
              deviceId: navigator.userAgent,
              deviceName: navigator.userAgent,
              deviceToken: token,
              platform: "WEB",
            },
          });
        });

        navigate(ROUTES.PATHS.SL_HOME);
      } else {
        setErrorMessage(result?.errorMessage);
      }
    } catch (e) {
      setLoading(false);
      setErrorMessage("Invalid login name or password.");
      message.error("Invalid login name or password.");
    }
  };

  const handleRegister = () => {
    navigate(ROUTES.PATHS.REGISTER);
  };

  const handleForgotPassword = () => {
    navigate(ROUTES.PATHS.FORGOT_PASSWORD);
  };

  return (
    <div className="login-form bg-idp-illustration h-screen bg-no-repeat bg-center grid place-content-center space-y-20 text-center">
      <div className="w-[250px] space-y-[10px]">
        <Form
          form={loginForm}
          layout="vertical"
          name="loginForm"
          validateMessages={validateMessages}
        >
          <div className="mb-5">
            <div className="mb-10">
              <img
                src="/serialman.png"
                style={{ width: "100%", cursor: "pointer" }}
                onClick={() =>
                  navigate(ROUTES.PATHS.SL_HOME)
                }
              />
            </div>
            <Form.Item
              name="loginName"
              rules={[{ required: true, message: "Required" }]}
            >
              <Input size="large" placeholder="Login name" prefix={<UserOutlined size={36} className="pr-1.5 text-black opacity-60" />} />
            </Form.Item>
            <Form.Item name="password" style={{ marginBottom: "0px" }} rules={[{ required: true, message: "Required" }]}>
              <Password size="large" placeholder={"Password"} prefix={<LockOutlined size={36} className="pr-1.5 text-black opacity-60" />} />
            </Form.Item>
            <div className="mb-1 text-right">
              <Button onClick={handleForgotPassword} type="link" className="text-primary pr-0 pl-0">{t("Forgot Password", { lng: systemLanguage })}</Button>
            </div>
            <div className="mb-2 text-left">
              <label style={{ color: "red", width: "100%", margin: "10px 0" }}>{errorMessage}</label>
            </div>
            <Button onClick={handleLogin} type="primary" block size="large">
              {t(authLoginResult.isLoading && loading ? "Please wait..." : "Login", { lng: systemLanguage })}
            </Button>
            <div className="mb-2 mt-3 text-right">
              <label>{t("Don't have an account?", { lng: systemLanguage })} <br /> <Button onClick={handleRegister} type="link" className="text-primary ml-[5px] pr-0 pl-0">{t("Register", { lng: systemLanguage })}</Button></label>
            </div>
          </div>
        </Form>

      </div>
    </div>
  );
};

export default Login;
